// === FEATURE: Cassette Deck ===
// Industrial brushed-metal tape deck. Bronze-rivet corners, two CSS-spinning
// reels, bouncing VU meters, and a working play/stop/next transport.
//
// This deck does NOT play any audio files. It is a GENERATIVE tape machine:
// every sound is synthesized in-code via WebAudio (see ./tapeEngine.js) —
// evolving drones, tape wow & flutter, distant found-recording textures,
// motor hum and hiss. It loads three+ distinct labeled "tapes", each a wholly
// different generative soundscape, with PLAY / STOP and a NEXT-tape transport.
//
// Character is deliberately distinct from StaticFM (radio static / glossolalia)
// and the MusicBumper (actual songs): no melody, no tuning — just slow, warped
// analog atmosphere from a machine playing half-remembered recordings.
//
// It claims the shared audioBus on play so it is mutually exclusive with
// StaticFM and the bumper (and exposes its live level so the faceless dancers
// react to it), releases on stop, and honors the global ~Music~ mute via the
// `sound` prop — muting stops the tape outright (you can't mute a synth that
// has no <audio> element, so the honest behaviour is to stop the machine).

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { FaStop, FaPlay, FaForward } from "react-icons/fa";
import { whenVisible } from "../_shared/sketchFrame";
import audioBus from "../_shared/audioBus";
import createTapeEngine from "./tapeEngine";
import "./cassettedeck.css";

const BUS_ID = "deck";

// Ten VU segments: green -> amber -> red.
const VU_COLORS = [
  "#12fe8d", "#12fe8d", "#12fe8d", "#12fe8d",
  "#ffa144", "#ffa144", "#ffa144",
  "#ff1900", "#ff1900", "#ff1900",
];

function CassetteDeck(props) {
  const muted = props.sound === false;

  const [isPlaying, setIsPlaying] = useState(false);
  const [tape, setTape] = useState(0);
  // Live 0..1 loudness, driven by a rAF loop while playing + on-screen. Feeds
  // the VU "lit segment" count so the meters react to the actual generated audio
  // rather than running a fixed canned animation.
  const [level, setLevel] = useState(0);
  // Visibility gate: false while the deck is scrolled off-screen OR the tab is
  // hidden. We freeze the reels CSS animation and pause the level rAF so the
  // deck burns zero CPU when nobody is looking. The AUDIO keeps playing (a real
  // tape deck doesn't stop because you looked away).
  const [visible, setVisible] = useState(true);

  const shellRef = useRef(null);
  const engineRef = useRef(null);
  const rafRef = useRef(0);
  // Keep a ref mirror of isPlaying so the bus pauseFn / effects read fresh state.
  const playingRef = useRef(false);

  // Lazily create the engine on first play (a user gesture, so the AudioContext
  // is allowed to start), reused thereafter. Disposed on unmount.
  function ensureEngine() {
    if (!engineRef.current) engineRef.current = createTapeEngine();
    return engineRef.current;
  }

  // Stop the machine + release the bus + drop the level loop. Used by STOP, by
  // the bus pauseFn (when StaticFM/bumper claim), and by the mute effect.
  function stopDeck() {
    playingRef.current = false;
    setIsPlaying(false);
    if (engineRef.current) engineRef.current.stop();
    audioBus.release(BUS_ID);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    setLevel(0);
  }

  function startDeck(index) {
    if (muted) return; // global ~Music~ off — stay silent.
    const eng = ensureEngine();
    eng.play(index);
    playingRef.current = true;
    setIsPlaying(true);
    // Claim the shared bus: pausing this source = stopping the tape; the level
    // provider lets audioBus.getLevel() read the generated audio while we own it.
    audioBus.claim(BUS_ID, stopDeck, () => eng.getLevel());
  }

  function togglePlay() {
    if (playingRef.current) {
      stopDeck();
    } else {
      startDeck(tape);
    }
  }

  // NEXT tape: advance the index. If currently playing, swap the soundscape
  // live; otherwise just arm the new tape for the next PLAY.
  function nextTape() {
    setTape((prev) => {
      const eng = engineRef.current;
      const count = eng ? eng.tapeCount : 4;
      const next = (prev + 1) % count;
      if (playingRef.current && eng) eng.play(next);
      return next;
    });
  }

  // Drive the VU level from the engine while playing AND on-screen.
  useEffect(() => {
    if (!(isPlaying && visible)) return;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      const eng = engineRef.current;
      setLevel(eng ? eng.getLevel() : 0);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [isPlaying, visible]);

  // IntersectionObserver + tab-visibility gate (reels/VU only; not the audio).
  useEffect(() => {
    const cleanup = whenVisible(
      shellRef.current,
      () => setVisible(true),
      () => setVisible(false)
    );
    return cleanup;
  }, []);

  // Global ~Music~ mute: there is no <audio> element to mute, so honestly stop
  // the machine when sound is turned off.
  useEffect(() => {
    if (muted && playingRef.current) stopDeck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  // Dispose the engine + drop any stale bus claim on unmount.
  useEffect(() => {
    return () => {
      audioBus.release(BUS_ID);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (engineRef.current) engineRef.current.dispose();
      engineRef.current = null;
    };
  }, []);

  const spinState = isPlaying && visible ? "running" : "paused";
  // Map 0..1 level onto how many VU segments are lit. A gentle curve so quiet
  // passages still show a couple of segments and loud swells push into the red.
  const lit = isPlaying ? Math.round(Math.min(1, level * 1.6) * VU_COLORS.length) : 0;
  const tapeName = engineRef.current
    ? engineRef.current.tapeName(tape)
    : ["DERELICT HOTEL", "TIDE / MAGNET", "ENGINE ROOM", "DISTANT CHOIR"][tape] || "";

  return (
    <div className="deck-shell" ref={shellRef}>
      {/* Four bronze rivets, one per corner */}
      <div className="deck-rivot deck-rivot-tl" />
      <div className="deck-rivot deck-rivot-tr" />
      <div className="deck-rivot deck-rivot-bl" />
      <div className="deck-rivot deck-rivot-br" />

      {/* Cassette viewing window with two spinning reels */}
      <div className="deck-window">
        <div className="reel" style={{ animationPlayState: spinState }} />
        <div className="tape-label">{tapeName}</div>
        <div className="reel" style={{ animationPlayState: spinState }} />
      </div>

      {/* VU meter row — segment count tracks the live generated loudness */}
      <div className="deck-vu">
        {VU_COLORS.map((c, i) => {
          const on = i < lit;
          return (
            <div
              key={i}
              className="vu-led"
              style={{
                background: c,
                opacity: on ? 1 : 0.28,
                transform: on ? "scaleY(1)" : "scaleY(0.4)",
                boxShadow: on ? "0 0 4px " + c + ", 0 0 2px " + c : "none",
              }}
            />
          );
        })}
      </div>

      {/* Tape / status readout */}
      <div className="deck-seek-wrap">
        <div className="deck-time deck-status">
          {isPlaying ? "▶ PLAYING" : "■ STOPPED"}
        </div>
      </div>

      {/* Transport buttons: STOP-or-PLAY (main) + NEXT tape */}
      <div className="deck-transport">
        <button
          className="deck-btn deck-btn-main"
          onClick={togglePlay}
          aria-label={isPlaying ? "stop" : "play"}
          title={muted ? "sound is off" : isPlaying ? "stop" : "play"}
        >
          {isPlaying ? <FaStop /> : <FaPlay />}
        </button>
        <button
          className="deck-btn"
          onClick={nextTape}
          aria-label="next tape"
          title="next tape"
        >
          <FaForward />
        </button>
      </div>
    </div>
  );
}

export default CassetteDeck;
